import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Package, Calendar, Eye, Plus, Filter, MapPin, Search, X, User, AlertTriangle, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
import { inventoryAuditService } from '@/services/inventoryAuditService';
import type { InventoryAudit } from '@/types/inventory-audit.types';

// Calcular rango lunes-domingo de la semana actual
const getWeekRange = () => {
  const now = new Date();
  const day = now.getDay(); // 0=domingo, 1=lunes...
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diffToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d: Date) => d.toISOString().split('T')[0];
  return { dateFrom: fmt(monday), dateTo: fmt(sunday) };
};

export const InventoryPage: React.FC = () => {
  const { user, selectedLocation } = useAuth();
  const navigate = useNavigate();

  // Helper function to check if user has a specific permission
  const hasPermission = (permissionCode: string): boolean => {
    return user?.permisos?.some(permiso => permiso.code === permissionCode) || false;
  };
  const [audits, setAudits] = useState<InventoryAudit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const weekRange = getWeekRange();
  // Filtros locales (solo se aplican al hacer clic en Buscar)
  const [filterInputs, setFilterInputs] = useState({
    locationId: 'all',
    dateFrom: weekRange.dateFrom,
    dateTo: weekRange.dateTo
  });
  // Filtros aplicados (los que realmente filtran la lista)
  const [appliedFilters, setAppliedFilters] = useState({
    locationId: 'all',
    dateFrom: weekRange.dateFrom,
    dateTo: weekRange.dateTo
  });
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [showNewInventoryModal, setShowNewInventoryModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModal, setMessageModal] = useState({
    type: 'info' as 'success' | 'error' | 'warning' | 'info',
    title: '',
    message: ''
  });
  const [inventoryForm, setInventoryForm] = useState({
    locationId: '',
    notes: ''
  });
  const [showFinalizedModal, setShowFinalizedModal] = useState(false);
  const [finalizedData, setFinalizedData] = useState({
    totalItems: 0,
    discrepancies: 0
  });
  const [accessibleLocations, setAccessibleLocations] = useState<Array<{ id: string; nombre: string }>>([]);
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Check if user is CAJERO (positionId: 3)
  const isCajero = user?.positionId === 3;

  // Load audits from API
  useEffect(() => {
    const loadAudits = async () => {
      try {
        setLoading(true);
        setError(null);

        const filters: Record<string, any> = {};
        // Cajero solo ve inventarios de su local seleccionado
        if (isCajero && selectedLocation?.id) {
          filters.locationId = selectedLocation.id;
        }
        const auditsResponse = await inventoryAuditService.getAudits(filters);
        if (auditsResponse.success && auditsResponse.data) {
          setAudits(auditsResponse.data || []);
        } else {
          setError(auditsResponse.error || 'Error al cargar los inventarios');
        }
      } catch (err) {
        setError('Error de conexión al cargar los inventarios');
        console.error('Error loading audits:', err);
      } finally {
        setLoading(false);
      }
    };

    loadAudits();
  }, [isCajero, selectedLocation]);

  // Load accessible locations separately
  useEffect(() => {
    // Función para obtener ubicación fallback
    const getFallbackLocation = () => {
      // Primero intentar con selectedLocation del contexto
      if (selectedLocation?.id) {
        return [{
          id: selectedLocation.id.toString(),
          nombre: selectedLocation.name || `Local ${selectedLocation.id}`
        }];
      }
      // Luego intentar con user.locationId
      if (user?.locationId) {
        return [{
          id: user.locationId.toString(),
          nombre: user.locationName || `Local ${user.locationId}`
        }];
      }
      return [];
    };

    const loadLocations = async () => {
      try {
        // Intentar con ambas claves de token
        const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
        if (!token) {
          console.error('No token found');
          setAccessibleLocations(getFallbackLocation());
          setLocationsLoaded(true);
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3003/api'}/purchase-orders/accessible-locations`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();

        if (data.success && Array.isArray(data.data) && data.data.length > 0) {
          const locs = data.data.map((loc: any) => ({
            id: loc.id.toString(),
            nombre: loc.nombre
          }));
          setAccessibleLocations(locs);
        } else {
          // Fallback: usar la ubicación del usuario/contexto
          setAccessibleLocations(getFallbackLocation());
        }
      } catch (err) {
        console.error('Error loading locations:', err);
        setAccessibleLocations(getFallbackLocation());
      } finally {
        setLocationsLoaded(true);
      }
    };

    loadLocations();
  }, [user?.locationId, user?.locationName, selectedLocation]);

  // Auto-select location for cajero based on selectedLocation from login
  useEffect(() => {
    if (isCajero && selectedLocation?.id && !inventoryForm.locationId) {
      setInventoryForm(prev => ({
        ...prev,
        locationId: selectedLocation.id.toString()
      }));
    }
  }, [isCajero, selectedLocation, inventoryForm.locationId]);

  // Check for finalized inventory flag
  useEffect(() => {
    const checkFinalizedInventory = () => {
      const finalizedData = localStorage.getItem('inventoryFinalized');
      if (finalizedData) {
        try {
          const data = JSON.parse(finalizedData);
          if (data.success) {
            setFinalizedData({
              totalItems: data.totalItems,
              discrepancies: data.discrepancies
            });
            setShowFinalizedModal(true);
            // Clean up the localStorage flag
            localStorage.removeItem('inventoryFinalized');
          }
        } catch (error) {
          console.error('Error parsing finalized inventory data:', error);
        }
      }
    };

    checkFinalizedInventory();
  }, []);

  // Helper function to show modal messages
  const showMessage = (type: 'success' | 'error' | 'warning' | 'info', title: string, message: string) => {
    setMessageModal({ type, title, message });
    setShowMessageModal(true);
  };

  const filteredAudits = audits.filter(audit => {
    // Cajeros solo ven inventarios en proceso
    if (isCajero && audit.status !== 'in_progress') return false;

    if (appliedFilters.locationId !== 'all' && audit.locationId.toString() !== appliedFilters.locationId) return false;
    if (selectedStatus !== 'all' && audit.status !== selectedStatus) return false;
    if (appliedFilters.dateFrom && audit.auditDate < appliedFilters.dateFrom) return false;
    if (appliedFilters.dateTo && audit.auditDate > appliedFilters.dateTo) return false;
    return true;
  });

  const handleStatusFilterChange = (status: string) => {
    setSelectedStatus(status);
  };

  const handleSearch = () => {
    setAppliedFilters({ ...filterInputs });
  };

  // Status filter options
  const statusFilters: Array<{ key: string, label: string, activeBg: string, borderColor: string, countColor: string }> = [
    { key: 'all', label: 'Todos', activeBg: 'bg-gray-700 text-white', borderColor: 'border-l-gray-400', countColor: 'text-gray-500' },
    { key: 'in_progress', label: 'En Proceso', activeBg: 'bg-amber-500 text-white', borderColor: 'border-l-amber-500', countColor: 'text-amber-600' },
    { key: 'completed', label: 'Completados', activeBg: 'bg-emerald-500 text-white', borderColor: 'border-l-emerald-500', countColor: 'text-emerald-600' },
    { key: 'reviewed', label: 'Revisados', activeBg: 'bg-sky-500 text-white', borderColor: 'border-l-sky-500', countColor: 'text-sky-600' }
  ];

  // Get count for each status (solo usado por roles que ven los filtros)
  const getStatusCount = (status: string) => {
    if (status === 'all') return audits.length;
    return audits.filter(audit => audit.status === status).length;
  };

  const handleViewDetails = (audit: InventoryAudit) => {
    navigate(`/inventory/audit/${audit.id}`);
  };

  const handleNewInventory = () => {
    setShowNewInventoryModal(true);
  };

  const handleCloseModal = () => {
    setShowNewInventoryModal(false);
    if (isCajero && selectedLocation?.id) {
      setInventoryForm({ locationId: selectedLocation.id.toString(), notes: '' });
    } else {
      setInventoryForm({ locationId: '', notes: '' });
    }
  };

  const handleFormSubmit = async () => {
    const locationId = inventoryForm.locationId ||
      (isCajero && selectedLocation?.id ? selectedLocation.id.toString() : '');

    if (!locationId) {
      showMessage('warning', 'Selección Requerida', 'Por favor selecciona una sucursal');
      return;
    }

    setIsCreating(true);

    try {
      // Use current date
      const currentDate = new Date().toISOString().split('T')[0];

      console.log('Creating audit with:', { locationId, auditDate: currentDate, notes: inventoryForm.notes });

      // Create audit via API
      const response = await inventoryAuditService.createAudit({
        locationId: locationId,
        auditDate: currentDate,
        notes: inventoryForm.notes
      });

      if (response.success && response.data) {
        // Close creation modal
        handleCloseModal();

        // Show success message
        showMessage('success', '¡Inventario Creado!', 'El inventario se ha creado exitosamente. Redirigiendo a la pantalla de toma...');

        // Redirect directly to the inventory audit detail page after a short delay
        setTimeout(() => {
          setShowMessageModal(false);
          navigate(`/inventory/audit/${response.data.id}`);
        }, 1500);

        // Refresh the audits list
        const auditsResponse = await inventoryAuditService.getAudits();
        if (auditsResponse.success && auditsResponse.data) {
          setAudits(auditsResponse.data.data || []);
        }
      } else {
        showMessage('error', 'Error al Crear Inventario', response.error || 'Error al crear el inventario');
      }
    } catch (error: any) {
      console.error('Error creating audit:', error);
      const errorMessage = error?.message || 'Error de conexión al crear el inventario';
      showMessage('error', 'Error al Crear', errorMessage);
    } finally {
      setIsCreating(false);
    }
  };



  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'reviewed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Completado';
      case 'in_progress': return 'En Proceso';
      case 'reviewed': return 'Revisado';
      default: return status;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-PE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Inventarios</h1>
          <p className="text-gray-600">Gestión de tomas de inventario por sucursal</p>
        </div>
        <Button
          className="bg-macartur-primary hover:bg-macartur-primary/90"
          onClick={handleNewInventory}
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Inventario
        </Button>
      </div>

      {/* Filters - Solo para roles no cajero */}
      {user?.positionId !== 3 && (
        <Card>
          <CardContent className="pt-4 pb-3 space-y-2">
            {/* Fila 1: Filtros de busqueda */}
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Sucursal:</label>
                <Select value={filterInputs.locationId} onValueChange={(value) => setFilterInputs(prev => ({ ...prev, locationId: value }))}>
                  <SelectTrigger className="h-9 w-48 text-sm">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las sucursales</SelectItem>
                    {accessibleLocations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Desde:</label>
                <Input
                  type="date"
                  value={filterInputs.dateFrom}
                  onChange={(e) => setFilterInputs(prev => ({ ...prev, dateFrom: e.target.value }))}
                  className="text-sm h-9 w-40"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Hasta:</label>
                <Input
                  type="date"
                  value={filterInputs.dateTo}
                  onChange={(e) => setFilterInputs(prev => ({ ...prev, dateTo: e.target.value }))}
                  className="text-sm h-9 w-40"
                />
              </div>
              <Button
                size="sm"
                className="h-9 bg-macartur-primary hover:bg-macartur-primary/90"
                onClick={handleSearch}
              >
                <Search className="mr-1 h-4 w-4" />
                Buscar
              </Button>
            </div>
            {/* Fila 2: Chips de estado */}
            <div className="flex flex-wrap items-center gap-1.5">
              {statusFilters.map((sf) => {
                const count = getStatusCount(sf.key);
                const isActive = selectedStatus === sf.key;
                return (
                  <button
                    key={sf.key}
                    onClick={() => handleStatusFilterChange(sf.key)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      isActive
                        ? sf.activeBg
                        : `bg-white border border-gray-200 border-l-[3px] ${sf.borderColor} text-gray-700 hover:bg-gray-50`
                    }`}
                  >
                    {sf.label}
                    <span className={`text-xs font-bold ${isActive ? 'text-white' : sf.countColor}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Inventory Audits List */}
      <Card>
        <CardHeader>
          <CardTitle>Inventarios Registrados</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <Package className="w-16 h-16 mx-auto mb-4 text-gray-300 animate-pulse" />
                <p className="text-gray-500">Cargando inventarios...</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">
              <Package className="w-16 h-16 mx-auto mb-4 text-red-300" />
              <h3 className="text-lg font-medium">Error al cargar inventarios</h3>
              <p>{error}</p>
              <Button
                onClick={() => window.location.reload()}
                className="mt-4"
                variant="outline"
              >
                Reintentar
              </Button>
            </div>
          ) : (
          <div className="space-y-4">
            {filteredAudits.map((audit) => (
              <div key={audit.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs font-mono text-gray-400">#{audit.id}</span>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-500" />
                        <span className="font-medium">{audit.locationName}</span>
                      </div>
                      <Badge className={getStatusColor(audit.status)}>
                        {getStatusText(audit.status)}
                      </Badge>
                      {audit.isRecount && (
                        <Badge className="bg-orange-100 text-orange-800">
                          🔄 Re-conteo
                        </Badge>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="font-bold">Fecha:</span>
                        <br />
                        {formatDate(audit.auditDate)}
                      </div>
                      <div>
                        <span className="font-bold">Auditor:</span>
                        <br />
                        {audit.auditorName}
                      </div>
                      <div>
                        <span className="font-bold">Productos:</span>
                        <br />
                        {audit.itemsAudited}/{audit.totalItems}
                      </div>
                      <div>
                        <span className="font-bold">Discrepancias:</span>
                        <br />
                        <span className={audit.discrepancies > 0 ? 'text-red-600' : 'text-green-600'}>
                          {audit.discrepancies}
                        </span>
                      </div>
                    </div>
                    
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewDetails(audit)}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Ver Detalle
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            
            {filteredAudits.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium">No se encontraron inventarios</h3>
                <p>Intenta ajustar los filtros o crear un nuevo inventario</p>
              </div>
            )}
          </div>
          )}
        </CardContent>
      </Card>

      {/* New Inventory Modal - usando Portal para renderizar fuera del layout */}
      {showNewInventoryModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/70"
            onClick={handleCloseModal}
          />
          {/* Modal Content */}
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Nuevo Inventario</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCloseModal}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <Label htmlFor="location" className="text-sm font-medium text-gray-700">
                  Sucursal *
                </Label>
                {!locationsLoaded ? (
                  <div className="mt-1 p-3 bg-gray-50 border rounded-md">
                    <span className="text-gray-500">Cargando ubicaciones...</span>
                  </div>
                ) : isCajero && selectedLocation ? (
                  <div className="mt-1 p-3 bg-gray-50 border rounded-md">
                    <div className="flex items-center gap-2 text-gray-700">
                      <MapPin className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">{selectedLocation.name}</span>
                    </div>
                  </div>
                ) : accessibleLocations.length === 0 ? (
                  <div className="mt-1 p-3 bg-red-50 border border-red-200 rounded-md">
                    <span className="text-red-600 text-sm">No tienes ubicaciones asignadas</span>
                  </div>
                ) : (
                  <Select
                    value={inventoryForm.locationId}
                    onValueChange={(value) => setInventoryForm(prev => ({ ...prev, locationId: value }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Seleccionar sucursal" />
                    </SelectTrigger>
                    <SelectContent className="z-[10000]">
                      {accessibleLocations.map(location => (
                        <SelectItem key={location.id} value={location.id}>
                          {location.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div>
                <Label htmlFor="auditor" className="text-sm font-medium text-gray-700">
                  Auditor
                </Label>
                <div className="mt-1 p-3 bg-gray-50 border rounded-md">
                  <div className="flex items-center gap-2 text-gray-700">
                    <User className="h-4 w-4 text-gray-500" />
                    <span className="font-medium">{user?.nombre} {user?.apellidos}</span>
                    <span className="text-sm text-gray-500">(usuario actual)</span>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="auditDate" className="text-sm font-medium text-gray-700">
                  Fecha de Auditoría
                </Label>
                <div className="mt-1 p-3 bg-gray-50 border rounded-md">
                  <div className="flex items-center gap-2 text-gray-700">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <span className="font-medium">{new Date().toLocaleDateString('es-PE', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}</span>
                    <span className="text-sm text-gray-500">(hoy)</span>
                  </div>
                </div>
              </div>

            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
              <Button variant="outline" onClick={handleCloseModal}>
                Cancelar
              </Button>
              <Button
                onClick={handleFormSubmit}
                className="bg-green-600 hover:bg-green-700"
                disabled={isCreating || (!inventoryForm.locationId && !(isCajero && accessibleLocations.length > 0))}
              >
                {isCreating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creando...
                  </>
                ) : (
                  <>
                    <Package className="h-4 w-4 mr-2" />
                    Crear e Iniciar Inventario
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Message Modal */}
      {showMessageModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowMessageModal(false)} />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                {messageModal.type === 'success' && (
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                )}
                {messageModal.type === 'error' && (
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <XCircle className="w-6 h-6 text-red-600" />
                  </div>
                )}
                {messageModal.type === 'warning' && (
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-yellow-600" />
                  </div>
                )}
                {messageModal.type === 'info' && (
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Package className="w-6 h-6 text-blue-600" />
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {messageModal.title}
                  </h3>
                </div>
              </div>

              <p className="text-gray-600 mb-6">
                {messageModal.message}
              </p>

              <div className="flex justify-end">
                <Button
                  onClick={() => setShowMessageModal(false)}
                  className={`${
                    messageModal.type === 'success' ? 'bg-green-600 hover:bg-green-700' :
                    messageModal.type === 'error' ? 'bg-red-600 hover:bg-red-700' :
                    messageModal.type === 'warning' ? 'bg-yellow-600 hover:bg-yellow-700' :
                    'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  Aceptar
                </Button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Finalized Inventory Modal */}
      {showFinalizedModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowFinalizedModal(false)} />
          <div className="relative bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="text-center">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                ¡Inventario Finalizado!
              </h2>
              <p className="text-gray-600 mb-6">
                El inventario ha sido completado exitosamente.
              </p>

              <Button
                onClick={() => setShowFinalizedModal(false)}
                className="bg-green-600 hover:bg-green-700 w-full"
              >
                Perfecto
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
};